import React, { useCallback, useEffect, useRef } from 'react';
import {
  AlertActionType,
  AlertPosition,
  AlertUrgency,
  ComponentColor,
  FdsIcon,
  Icon,
  showAlert,
} from '@tcc/fds';
import { getMessage } from '@tcc/i18n';
import { UrlPlaceholders } from './useNavigationLinks/types';
import { User } from '../types/user';
import { useExpertoBannerPreference } from './useExpertoBannerPreference';

const EXPERTO_IMPORT_FIELDS_URL = `${UrlPlaceholders.EXPERTO_URL}/import-fields`;

interface UseExpertoBannerParams {
  user?: User;
  featureFlags: Record<string, boolean | undefined>;
  containerElement?: HTMLDivElement | null;
  onOpenDialog?: () => void;
  onCloseDialog?: () => void;
  isDialogOpen?: boolean;
}

export const useExpertoBanner = ({
  user,
  featureFlags,
  containerElement,
  onOpenDialog,
  onCloseDialog,
  isDialogOpen = false,
}: UseExpertoBannerParams) => {
  const hasTriggeredRef = useRef(false);
  const hasFetchedPreferenceRef = useRef(false);
  const isArgentinaUser = user && user.details && user.details.countryCode === 'AR';
  const isExpertoBannerEnabledForUser =
    !!user?.uuid
    && featureFlags?.['web.fieldview.experto-banner'] === true
    && isArgentinaUser;

  const {
    fetchPreference,
    showBanner,
    preferenceId,
    updatePreference,
  } = useExpertoBannerPreference();

  useEffect(() => {
    if (!isExpertoBannerEnabledForUser || !user?.uuid) {
      hasFetchedPreferenceRef.current = false;
      return;
    }

    if (hasFetchedPreferenceRef.current) {
      return;
    }

    hasFetchedPreferenceRef.current = true;
    fetchPreference({ variables: { userId: user.uuid.toString() } }).catch(() => { });
  }, [fetchPreference, isExpertoBannerEnabledForUser, user]);

  const handleConfirmHideExpertoBanner = useCallback(async () => {
    if (!user?.uuid || !preferenceId) {
      onCloseDialog?.();
      return;
    }

    await updatePreference({
      variables: {
        userId: user.uuid.toString(),
        preferenceId,
        showBanner: false,
      },
    });

    onCloseDialog?.();
  }, [onCloseDialog, preferenceId, updatePreference, user]);

  useEffect(() => {
    if (isDialogOpen) {
      hasTriggeredRef.current = false;
      return;
    }

    if (hasTriggeredRef.current) {
      return;
    }
    if (
      containerElement
      && isExpertoBannerEnabledForUser
      && showBanner === true
    ) {
      hasTriggeredRef.current = true;

      const alertContainerRef = { current: containerElement } as React.RefObject<HTMLDivElement>;

      showAlert({
        position: AlertPosition.Banner,
        urgency: AlertUrgency.Medium,
        color: ComponentColor.Warning,
        icon: React.createElement(Icon, { icon: FdsIcon.Info }),
        dismissible: false,
        children: getMessage('experto.alert.label'),
        containerRef: alertContainerRef,
        actions: [
          {
            type: AlertActionType.Secondary,
            label: getMessage('experto.alert.dismiss'),
            testId: 'experto-alert-banner-secondary',
            buttonProps: {
              onClick: onOpenDialog,
            },
          },
          {
            type: AlertActionType.Primary,
            label: getMessage('experto.alert.importFields'),
            testId: 'experto-alert-banner-primary',
            buttonProps: {
              onClick: () => window.location.assign(EXPERTO_IMPORT_FIELDS_URL),
            },
          },
        ],
        persistent: true,
      });
    }
  }, [containerElement, isDialogOpen, isExpertoBannerEnabledForUser, onOpenDialog, showBanner]);

  return { handleConfirmHideExpertoBanner };
};
